import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Accessibility } from "@mui/icons-material";
import { TEXT_SCALE_OPTIONS } from "./publicSiteA11yPreferences";
import "./publicSiteA11y.scss";

export default function PublicSiteAccessibilityWidget({ prefs, onPrefsChange }) {
  const panelId = useId();
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);

  const updatePref = useCallback(
    (patch) => {
      onPrefsChange({ ...prefs, ...patch });
    },
    [onPrefsChange, prefs]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      const t = e.target;
      if (
        panelRef.current?.contains(t) ||
        triggerRef.current?.contains(t)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const first = panelRef.current?.querySelector(
      "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
    );
    if (first && typeof first.focus === "function") first.focus();
  }, [open]);

  return (
    <>
      <a href="#vp-public-main" className="public-site-a11y-skip-link">
        דלג לתוכן הראשי
      </a>
      <div className="public-site-a11y-widget" dir="rtl" lang="he">
        {open ? (
          <div
            ref={panelRef}
            id={panelId}
            className="public-site-a11y-widget__panel"
            role="dialog"
            aria-modal="false"
            aria-labelledby={`${panelId}-title`}
          >
            <h2 id={`${panelId}-title`} className="public-site-a11y-widget__title">
              נגישות
            </h2>
            <div className="public-site-a11y-widget__group">
              <span className="public-site-a11y-widget__label" id={`${panelId}-scale`}>
                גודל טקסט
              </span>
              <div
                className="public-site-a11y-widget__scale-row"
                role="group"
                aria-labelledby={`${panelId}-scale`}
              >
                {TEXT_SCALE_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`public-site-a11y-widget__scale-btn${
                      prefs.textScale === opt.id ? " public-site-a11y-widget__scale-btn--active" : ""
                    }`}
                    aria-pressed={prefs.textScale === opt.id}
                    onClick={() => updatePref({ textScale: opt.id })}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="public-site-a11y-widget__group">
              <button
                type="button"
                className="public-site-a11y-widget__toggle"
                aria-pressed={prefs.highContrast}
                onClick={() => updatePref({ highContrast: !prefs.highContrast })}
              >
                <span>ניגודיות מוגברת</span>
                <span aria-hidden>{prefs.highContrast ? "פעיל" : "כבוי"}</span>
              </button>
            </div>
            <div className="public-site-a11y-widget__group">
              <button
                type="button"
                className="public-site-a11y-widget__toggle"
                aria-pressed={prefs.underlineLinks}
                onClick={() => updatePref({ underlineLinks: !prefs.underlineLinks })}
              >
                <span>הדגשת קישורים</span>
                <span aria-hidden>{prefs.underlineLinks ? "פעיל" : "כבוי"}</span>
              </button>
            </div>
            <div className="public-site-a11y-widget__group">
              <button
                type="button"
                className="public-site-a11y-widget__toggle"
                aria-pressed={prefs.reduceMotion}
                onClick={() => updatePref({ reduceMotion: !prefs.reduceMotion })}
              >
                <span>הפחתת תנועה</span>
                <span aria-hidden>{prefs.reduceMotion ? "פעיל" : "כבוי"}</span>
              </button>
            </div>
            <button
              type="button"
              className="public-site-a11y-widget__close"
              onClick={() => {
                setOpen(false);
                triggerRef.current?.focus();
              }}
            >
              סגור
            </button>
          </div>
        ) : null}
        <button
          ref={triggerRef}
          type="button"
          className="public-site-a11y-widget__trigger"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          aria-haspopup="dialog"
          aria-label={open ? "סגור תפריט נגישות" : "פתח תפריט נגישות"}
          title="נגישות"
          onClick={() => setOpen((v) => !v)}
        >
          <Accessibility className="public-site-a11y-widget__trigger-icon" aria-hidden />
        </button>
      </div>
    </>
  );
}
